import { dataContext } from '@sdk';
import { OpenProjectComponent } from '@src/components/open-project-component/open-project-component';
import { normalizePath, useProjectOpener } from '@src/components/open-project-component/use-open-project';
import { NewProjectDialog, NewProjectFromGitDialog, useGitCloneDialogSubmit } from '@src/components/project-selector';
import { notify } from '@src/notifications';
import { DesktopTile } from '@src/components/quick-create/QuickCreatePanel';
import { useProjects } from '@src/hooks/use-projects';
import { FolderOpen, FolderPlus, FolderSearch, GitBranch, Loader2 } from 'lucide-react';
import { useState, type ComponentType } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useActiveWorkspace } from '@src/hooks/use-workspaces';

/**
 * Compact outlined-button presentation — the Vibe hero's under-input strip,
 * read as one sentence: "Select project [new] [folder] [existing] [git]".
 * Bordered, filled and foreground-coloured so each action reads as a button,
 * not as muted hint text.
 */
const LINK_CLASS =
  'inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-xs font-medium text-foreground shadow-sm transition-colors hover:border-foreground/30 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/** One action, in whichever presentation the host asked for. */
function ActionButton({
  variant,
  Icon,
  label,
  shortLabel,
  loading,
  onClick,
  testId,
}: {
  variant: 'tiles' | 'links';
  Icon: ComponentType<{ className?: string }>;
  label: string;
  /** The one-word chip text of the `links` strip; `label` stays its tooltip. */
  shortLabel: string;
  loading?: boolean;
  onClick: () => void;
  testId: string;
}) {
  if (variant === 'tiles') {
    return <DesktopTile Icon={Icon} label={label} loading={loading} onClick={onClick} data-testid={testId} />;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={LINK_CLASS}
      title={label}
      aria-label={label}
      data-testid={testId}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
      {shortLabel}
    </button>
  );
}

/**
 * The project-actions row — New project / Open folder / Open existing project /
 * Open from git — plus the three dialogs they drive.
 *
 * Two presentations of the SAME actions, chosen by the host: `tiles` (the
 * icon-over-label square project home uses for its "New …" affordances — hub
 * home) and `links` (the Vibe hero's slim under-input strip). Only the shape
 * differs; the action set and every flow behind it stay in this one component.
 *
 * One component, shared by every home surface that offers "get me into a
 * project" (the Vibe hero and the hub page's Projects section), so the set of
 * affordances and their flows can't drift apart.
 *
 * The hub has projects and a current project like the desktop does — it just
 * reaches its files over the VFS with git as the filesystem. So only ONE action
 * is desk-specific: **Open folder**, which drives the host's native folder
 * picker, shown only where an OS window reaches a person (`caps.osDialogs`).
 * It is the only ACTION dropped there; open-existing / new / from-git all work
 * against the hub. The New-project dialog also drops its whole folder row on
 * the hub (`withFolder`): a hub project is a pure entity with nothing on a
 * filesystem behind it, and with the field still required — and no native
 * picker or workspace default able to fill it — Create could never enable.
 *
 * **Open existing project** is gated on there BEING one: with an empty project
 * list the picker it opens has nothing to pick, so the tile would be a dead end.
 * The create-flavoured actions always show, so an empty home is never a wall.
 */
export function ProjectActionsRow({
  className = '',
  variant = 'links',
}: {
  className?: string;
  variant?: 'tiles' | 'links';
}) {
  const { t } = useLingui();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [isGitProjectOpen, setIsGitProjectOpen] = useState(false);
  const [isOpeningFolder, setIsOpeningFolder] = useState(false);
  // New projects go in the ACTIVE workspace's folder (the default root when there is one workspace).
  const { root: defaultWorkspacePath } = useActiveWorkspace();
  const { projects } = useProjects();
  const hasProjects = !!projects && projects.length > 0;

  // On a home surface, opening/switching a project just changes the project and
  // lands on the fresh home (never resumes an old build process) — that
  // decision lives inside useProjectOpener, derived from the current surface.
  const { openProjectFolder, pickFolder, ensureProjectAndSetContext, openExistingProject, computeNode } =
    useProjectOpener({
      onError: (message) => notify.error({ title: message }),
    });

  // Clone + open, with this surface's landing: from home we stay home (on the
  // fresh hero for the new project) rather than jumping to its dock.
  const handleCreateGitProject = useGitCloneDialogSubmit(computeNode?.id, openExistingProject);

  const handleOpenFolder = async () => {
    // openProjectFolder never throws — it routes failures through onError.
    setIsOpeningFolder(true);
    try {
      await openProjectFolder();
    } finally {
      setIsOpeningFolder(false);
    }
  };

  // Native host folder picker: only where an OS window reaches a person — not the hub,
  // not a cloud box (its picker runs on a machine with no screen).
  const { osDialogs: canPickHostFolder, localFiles } = dataContext.caps;

  return (
    <>
      <div
        className={`flex flex-wrap items-center ${variant === 'tiles' ? 'gap-3' : 'gap-1.5'} ${className}`}
        data-testid="project-actions-row"
      >
        {variant === 'links' && (
          <span className="text-xs text-muted-foreground" data-testid="project-actions-label">
            {t`Select project`}
          </span>
        )}
        <ActionButton
          variant={variant}
          Icon={FolderPlus}
          label={t`New project`}
          shortLabel={t`new`}
          onClick={() => setIsNewProjectOpen(true)}
          testId="vibe-new-project"
        />
        {canPickHostFolder && (
          <ActionButton
            variant={variant}
            Icon={FolderOpen}
            label={t`Open folder`}
            shortLabel={t`folder`}
            loading={isOpeningFolder}
            onClick={() => void handleOpenFolder()}
            testId="vibe-open-project-folder"
          />
        )}
        {hasProjects && (
          <ActionButton
            variant={variant}
            Icon={FolderSearch}
            label={t`Open project`}
            shortLabel={t`existing`}
            onClick={() => setIsProjectModalOpen(true)}
            testId="vibe-open-existing-project"
          />
        )}
        <ActionButton
          variant={variant}
          Icon={GitBranch}
          label={t`Open from git`}
          shortLabel={t`git`}
          onClick={() => setIsGitProjectOpen(true)}
          testId="vibe-open-from-git"
        />
      </div>
      <OpenProjectComponent open={isProjectModalOpen} onOpenChange={setIsProjectModalOpen} />
      <NewProjectDialog
        open={isNewProjectOpen}
        onOpenChange={setIsNewProjectOpen}
        defaultParentFolder={defaultWorkspacePath}
        // A hub project is a pure entity — no folder behind it — so the dialog
        // asks for a name only. On the desk the project IS a directory, so the
        // parent folder stays required.
        withFolder={localFiles}
        onPickFolder={canPickHostFolder ? () => pickFolder(defaultWorkspacePath || undefined) : undefined}
        onCreate={async (name, parentFolder) => {
          await ensureProjectAndSetContext(parentFolder ? `${normalizePath(parentFolder)}/${name}` : name);
        }}
      />
      {/* Mounted only while open — keeps the repo/branch pickers out of the
          home route's eager module graph. */}
      {isGitProjectOpen && (
        <NewProjectFromGitDialog open onOpenChange={setIsGitProjectOpen} onCreate={handleCreateGitProject} />
      )}
    </>
  );
}
